import axios from "axios";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  DASHBOARD,
  COUNTRY_LIST_DROPDOWN,
  STATE_LIST_DROPDOWN,
  CITY_LIST_DROPDOWN,
  REGION_LIST_DROPDOWN,
  CHAPTER_LIST_DROPDOWN,
} from "../../routes/routes";
import Select from "react-select";
import { hasRole } from "../../components/Utils";
import { usePermission } from "../../hooks/usePermission";
import moment from "moment";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_red.css";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";
import "./Dashboard.css";

// Dashboard widget components
import AnalyticsCards from "./components/AnalyticsCards";
import ChartSwiper from "./components/ChartSwiper";
import TopAchievers from "./components/TopAchievers";
import BirthdayCard from "./components/BirthdayCard";
import PendingListsTables from "./components/PendingListsTables";
import TopTenTables from "./components/TopTenTables";

const Dashboard = () => {
  const { hasPermission } = usePermission();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ovalLoading, setOvalLoading] = useState(true);
  const [countryOptions, setCountryOptions] = useState({});
  const [stateOptions, setStateOptions] = useState({});
  const [cityOptions, setCityOptions] = useState({});
  const [regionOptions, setRegionOptions] = useState([]);
  const [chapterOptions, setChapterOptions] = useState([]);
  const [filter, setFilter] = useState([]);
  const [showClearBtn, setShowClearBtn] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const refFlatpickr = useRef(null);
  const filterPanelRef = useRef(null);
  const filterBtnRef = useRef(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const selectStateRef = useRef(null);
  const selectCityRef = useRef(null);
  const selectRegionRef = useRef(null);
  const selectChapterRef = useRef(null);

  // Count active location filters
  const activeFilterCount = [
    filter.country_id,
    filter.state_id,
    filter.city_id,
    filter.region_id,
    filter.chapter_id,
  ].filter(Boolean).length;

  // Close filter panel on outside click
  const handleClickOutside = useCallback((e) => {
    if (
      filterPanelRef.current &&
      !filterPanelRef.current.contains(e.target) &&
      filterBtnRef.current &&
      !filterBtnRef.current.contains(e.target)
    ) {
      setShowFiltersPanel(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  // Shared styles for Select dropdowns inside filter panel
  const selectFilterStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    control: (base) => ({ ...base, fontSize: "0.85rem", minHeight: "36px" }),
    placeholder: (base) => ({
      ...base,
      fontSize: "0.85rem",
      color: "#222",
      fontWeight: "500",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "0.85rem",
      color: "#222",
      fontWeight: "500",
    }),
    option: (base) => ({ ...base, fontSize: "0.85rem", color: "#222" }),
    input: (base) => ({ ...base, fontSize: "0.85rem", color: "#222" }),
  };

  const handleChapterClear = () => {
    if (selectChapterRef.current) selectChapterRef.current.clearValue();
    if (refFlatpickr.current && refFlatpickr.current.flatpickr) {
      refFlatpickr.current.flatpickr.clear();
      setShowClearBtn(false);
    }
  };

  const handleRegionClear = () => {
    if (selectRegionRef.current) selectRegionRef.current.clearValue();
    handleChapterClear();
  };

  const handleCityClear = () => {
    if (selectCityRef.current) selectCityRef.current.clearValue();
    handleRegionClear();
  };

  const handleStateClear = () => {
    if (selectStateRef.current) selectStateRef.current.clearValue();
    handleCityClear();
  };

  const handleDownloadPDF = async (reportType) => {
    try {
      setPdfLoading(true);
      // Create a new PDF document
      const currentDate = moment().format("DD-MM-YYYY");
      const doc = new jsPDF("landscape");
      var headers = [];
      var pdfData = [];
      var fileName = "";
      var pdfTitle = "";

      headers = ["Sl.No.", "Member"];

      if (reportType === "top_10_points_members") {
        if (
          !data.top10PointsUsersList ||
          data.top10PointsUsersList.length === 0
        ) {
          toast.error("No data available to export");
          return;
        }
        // Define table headers and data
        headers.push("Points");
        pdfData = data.top10PointsUsersList.map((data) => {
          let pData = [data.sno, data.name, data.points];
          if (hasRole([1])) {
            pData.push(data.country_name);
          }
          if (hasRole([1, 6])) {
            pData.push(data.state_name);
          }
          if (hasRole([1, 6, 7])) {
            pData.push(data.city_name);
          }
          if (hasRole([1, 6, 7, 8])) {
            pData.push(data.region_name);
          }
          if (hasRole([1, 6, 7, 8, 2])) {
            pData.push(data.chapter_name);
          }
          return pData;
        });
        fileName = `Top10Points_${currentDate}.pdf`;
        pdfTitle = `Top 10 Members by Points`;
      } else if (reportType === "top_10_business_given_members") {
        if (
          !data.top10BusinessUsersList ||
          data.top10BusinessUsersList.length === 0
        ) {
          toast.error("No data available to export");
          return;
        }
        // Define table headers and data
        headers.push("Business Value");
        pdfData = data.top10BusinessUsersList.map((data) => {
          let pData = [
            data.sno,
            data.name,
            parseFloat(data.business_value_without_format).toLocaleString(
              "en-IN",
            ),
          ];
          if (hasRole([1])) {
            pData.push(data.country_name);
          }
          if (hasRole([1, 6])) {
            pData.push(data.state_name);
          }
          if (hasRole([1, 6, 7])) {
            pData.push(data.city_name);
          }
          if (hasRole([1, 6, 7, 8])) {
            pData.push(data.region_name);
          }
          if (hasRole([1, 6, 7, 8, 2])) {
            pData.push(data.chapter_name);
          }
          return pData;
        });
        fileName = `Top10Business_${currentDate}.pdf`;
        pdfTitle = `Top 10 Members by Business Value`;
      } else if (reportType === "top_10_referral_given_members") {
        if (
          !data.top10ReferralsGivenUsersList ||
          data.top10ReferralsGivenUsersList.length === 0
        ) {
          toast.error("No data available to export");
          return;
        }
        // Define table headers and data
        headers.push("Professional Connects Count");
        pdfData = data.top10ReferralsGivenUsersList.map((data) => {
          let pData = [data.sno, data.name, data.referrals_count];
          if (hasRole([1])) {
            pData.push(data.country_name);
          }
          if (hasRole([1, 6])) {
            pData.push(data.state_name);
          }
          if (hasRole([1, 6, 7])) {
            pData.push(data.city_name);
          }
          if (hasRole([1, 6, 7, 8])) {
            pData.push(data.region_name);
          }
          if (hasRole([1, 6, 7, 8, 2])) {
            pData.push(data.chapter_name);
          }
          return pData;
        });
        fileName = `Top10Referrals_${currentDate}.pdf`;
        pdfTitle = `Top 10 Members by Professional Connects Given Count`;
      }
      // Client: General Visitors paused — comment only, do not remove
      // else if (reportType === "top_10_visitors_members") {
      //   if (
      //     !data.top10VisitorsGivenUsersList ||
      //     data.top10VisitorsGivenUsersList.length === 0
      //   ) {
      //     toast.error("No data available to export");
      //     return;
      //   }
      //   headers.push("Visitors Count");
      //   pdfData = data.top10VisitorsGivenUsersList.map((data) => {
      //     let pData = [data.sno, data.name, data.visitors_count];
      //     if (hasRole([1])) { pData.push(data.country_name); }
      //     if (hasRole([1, 6])) { pData.push(data.state_name); }
      //     if (hasRole([1, 6, 7])) { pData.push(data.city_name); }
      //     if (hasRole([1, 6, 7, 8])) { pData.push(data.region_name); }
      //     if (hasRole([1, 6, 7, 8, 2])) { pData.push(data.chapter_name); }
      //     return pData;
      //   });
      //   fileName = `Top10Visitors_${currentDate}.pdf`;
      //   pdfTitle = `Top 10 Members by Visitors Given Count`;
      // }
      else if (reportType === "top_10_121_members") {
        if (
          !data.top10121sGivenUsersList ||
          data.top10121sGivenUsersList.length === 0
        ) {
          toast.error("No data available to export");
          return;
        }
        // Define table headers and data
        headers.push("Personal Meets Count");
        pdfData = data.top10121sGivenUsersList.map((data) => {
          let pData = [data.sno, data.name, data.one2one_count];
          if (hasRole([1])) {
            pData.push(data.country_name);
          }
          if (hasRole([1, 6])) {
            pData.push(data.state_name);
          }
          if (hasRole([1, 6, 7])) {
            pData.push(data.city_name);
          }
          if (hasRole([1, 6, 7, 8])) {
            pData.push(data.region_name);
          }
          if (hasRole([1, 6, 7, 8, 2])) {
            pData.push(data.chapter_name);
          }
          return pData;
        });
        fileName = `Top10121s_${currentDate}.pdf`;
        pdfTitle = `Top 10 Members by Personal Meets Given Count`;
      } else if (reportType === "top_10_inductions_members") {
        if (
          !data.top10InductionsGivenUsersList ||
          data.top10InductionsGivenUsersList.length === 0
        ) {
          toast.error("No data available to export");
          return;
        }
        // Define table headers and data
        headers.push("Inductions Count");
        pdfData = data.top10InductionsGivenUsersList.map((data) => {
          let pData = [data.sno, data.name, data.inductions_count];
          if (hasRole([1])) {
            pData.push(data.country_name);
          }
          if (hasRole([1, 6])) {
            pData.push(data.state_name);
          }
          if (hasRole([1, 6, 7])) {
            pData.push(data.city_name);
          }
          if (hasRole([1, 6, 7, 8])) {
            pData.push(data.region_name);
          }
          if (hasRole([1, 6, 7, 8, 2])) {
            pData.push(data.chapter_name);
          }
          return pData;
        });
        fileName = `Top10Inductions_${currentDate}.pdf`;
        pdfTitle = `Top 10 Members by Inductions Given Count`;
      }
      // Client: Chief Guests / Power Dates paused — comment only, do not remove
      // else if (reportType === "top_10_chief_guests_members") {
      //   if (
      //     !data.top10ChiefGuestGivenUsersList ||
      //     data.top10ChiefGuestGivenUsersList.length === 0
      //   ) {
      //     toast.error("No data available to export");
      //     return;
      //   }
      //   headers.push("Chief Guests Count");
      //   pdfData = data.top10ChiefGuestGivenUsersList.map((data) => {
      //     let pData = [data.sno, data.name, data.chief_guests_count];
      //     if (hasRole([1])) { pData.push(data.country_name); }
      //     if (hasRole([1, 6])) { pData.push(data.state_name); }
      //     if (hasRole([1, 6, 7])) { pData.push(data.city_name); }
      //     if (hasRole([1, 6, 7, 8])) { pData.push(data.region_name); }
      //     if (hasRole([1, 6, 7, 8, 2])) { pData.push(data.chapter_name); }
      //     return pData;
      //   });
      //   fileName = `Top10ChiefGuests_${currentDate}.pdf`;
      //   pdfTitle = `Top 10 Members by Chief Guests Count`;
      // } else if (reportType === "top_10_power_dates_members") {
      //   if (
      //     !data.top10PowerDateGivenUsersList ||
      //     data.top10PowerDateGivenUsersList.length === 0
      //   ) {
      //     toast.error("No data available to export");
      //     return;
      //   }
      //   headers.push("Power Dates Count");
      //   pdfData = data.top10PowerDateGivenUsersList.map((data) => {
      //     let pData = [data.sno, data.name, data.power_dates_count];
      //     if (hasRole([1])) { pData.push(data.country_name); }
      //     if (hasRole([1, 6])) { pData.push(data.state_name); }
      //     if (hasRole([1, 6, 7])) { pData.push(data.city_name); }
      //     if (hasRole([1, 6, 7, 8])) { pData.push(data.region_name); }
      //     if (hasRole([1, 6, 7, 8, 2])) { pData.push(data.chapter_name); }
      //     return pData;
      //   });
      //   fileName = `Top10PowerDates_${currentDate}.pdf`;
      //   pdfTitle = `Top 10 Members by Power Dates Count`;
      // }

      if (hasRole([1])) {
        headers.push("Country");
      }
      if (hasRole([1, 6])) {
        headers.push("State");
      }
      if (hasRole([1, 6, 7])) {
        headers.push("City");
      }
      if (hasRole([1, 6, 7, 8])) {
        headers.push("Region");
      }
      if (hasRole([1, 6, 7, 8, 2])) {
        headers.push("Circle");
      }

      //pdf title starts
      const textWidth =
        (doc.getStringUnitWidth(pdfTitle) * doc.internal.getFontSize()) /
        doc.internal.scaleFactor;
      const textOffset = (doc.internal.pageSize.width - textWidth) / 2;
      doc.text(pdfTitle, textOffset, 10); // Centered title at the top
      //pdf title ends

      // Add the table to the document
      doc.autoTable({
        head: [headers],
        body: pdfData,
        margin: { top: 20, right: 20, bottom: 20, left: 20 }, // Adjust margins
        startY: 20, // Start position of the table from top
        headerStyles: { fillColor: [207, 32, 48], textColor: [255, 255, 255] }, // Change header color
        styles: { lineWidth: 0.1 }, // Set border width for table cells
      });
      // Save the PDF
      doc.save(fileName);
    } catch (error) {
      console.error("<<<ERRRROR>>>", error);
      console.error("Something Went Wrong", error.message);
      toast.error(error.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const auth_user = JSON.parse(localStorage.getItem("user"));
  const isSuperAdmin = auth_user?.user_type === 1 ? 1 : 0;
  const isRegionAdmin = auth_user?.user_type === 2 ? 1 : 0;

  const handleDateRangeClear = () => {
    setShowClearBtn(false);
    if (refFlatpickr.current && refFlatpickr.current.flatpickr) {
      refFlatpickr.current.flatpickr.setDate([]);
    }
    setFilter({
      ...filter,
      from_date: "",
      to_date: "",
      quick_range: "",
    });
  };

  useEffect(() => {
    setOvalLoading(true);
    setLoading(true);
    axios
      .get(DASHBOARD, {
        params: {
          isAdmin: 1,
          ...filter,
        },
      })
      .then((response) => {
        if (response.status === 200 && response.data.success) {
          setData(response.data.data);
        }
      })
      .finally(() => {
        setOvalLoading(false);
        setLoading(false); // Set loading to false after data is fetched
      });

    const fetchData = async () => {
      try {
        // Fetch country data
        const countryResponse = await axios.get(COUNTRY_LIST_DROPDOWN);
        const fetchedCountryOptions = countryResponse.data.data;
        setCountryOptions(fetchedCountryOptions);

        // Fetch state data
        const stateResponse = await axios.get(STATE_LIST_DROPDOWN, {
          params: {
            ...filter,
          },
        });
        const fetchedStateOptions = stateResponse.data.data;
        setStateOptions(fetchedStateOptions);

        const cityResponse = await axios.get(CITY_LIST_DROPDOWN, {
          params: {
            ...filter,
          },
        });
        setCityOptions(cityResponse.data.data);

        // Fetch region data
        const regionResponse = await axios.get(REGION_LIST_DROPDOWN, {
          params: {
            ...filter,
          },
        });
        const fetchedRegionOptions = regionResponse.data.data;
        setRegionOptions(fetchedRegionOptions);

        // Fetch chapter data
        const chapterResponse = await axios.get(CHAPTER_LIST_DROPDOWN, {
          params: {
            ...filter,
          },
        });
        const fetchedChapterOptions = chapterResponse.data.data;
        setChapterOptions(fetchedChapterOptions);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [filter]);

  const handleCalendarClick = () => {
    if (refFlatpickr.current) {
      refFlatpickr.current.flatpickr.open();
    }
  };

  return (
    <div className="content-wrapper">
      <div className="flex-grow-1">
        <div className="container-fluid dp-wrapper px-0">
          {/* Analytics Welcome Header */}
          <div className="dashboard-analytics-header">
            <div className="analytics-header-left">
              <h1 className="analytics-title d-flex align-items-center">
                <i
                  className="bx bxs-dashboard me-2"
                  style={{
                    color: "#cf2030",
                    fontSize: "1.1em",
                    marginTop: "-2px",
                  }}
                ></i>
                Dashboard
              </h1>
              {/* <p className="analytics-subtitle">
                Track chapter performance, member engagement, referrals, and
                networking activities.
              </p> */}
            </div>
            <div className="analytics-header-right">
              {/* <div className="analytics-quick-filters">
                <button
                  className={`quick-filter-btn ${filter.quick_range === "7d" ? "active" : ""}`}
                  onClick={() => {
                    const to = moment().format("DD/MM/YYYY");
                    const from = moment()
                      .subtract(7, "days")
                      .format("DD/MM/YYYY");
                    setShowClearBtn(true);
                    if (refFlatpickr.current) {
                      refFlatpickr.current.flatpickr.setDate([from, to]);
                    }
                    setFilter({
                      ...filter,
                      from_date: from,
                      to_date: to,
                      quick_range: "7d",
                    });
                  }}
                >
                  7d
                </button>
                <button
                  className={`quick-filter-btn ${filter.quick_range === "30d" ? "active" : ""}`}
                  onClick={() => {
                    const to = moment().format("DD/MM/YYYY");
                    const from = moment()
                      .subtract(30, "days")
                      .format("DD/MM/YYYY");
                    setShowClearBtn(true);
                    if (refFlatpickr.current) {
                      refFlatpickr.current.flatpickr.setDate([from, to]);
                    }
                    setFilter({
                      ...filter,
                      from_date: from,
                      to_date: to,
                      quick_range: "30d",
                    });
                  }}
                >
                  30d
                </button>
                <button
                  className={`quick-filter-btn ${filter.quick_range === "6m" ? "active" : ""}`}
                  onClick={() => {
                    const to = moment().format("DD/MM/YYYY");
                    const from = moment()
                      .subtract(6, "months")
                      .format("DD/MM/YYYY");
                    setShowClearBtn(true);
                    if (refFlatpickr.current) {
                      refFlatpickr.current.flatpickr.setDate([from, to]);
                    }
                    setFilter({
                      ...filter,
                      from_date: from,
                      to_date: to,
                      quick_range: "6m",
                    });
                  }}
                >
                  6m
                </button>
                <button
                  className={`quick-filter-btn ${filter.quick_range === "1y" ? "active" : ""}`}
                  onClick={() => {
                    const to = moment().format("DD/MM/YYYY");
                    const from = moment()
                      .subtract(1, "year")
                      .format("DD/MM/YYYY");
                    setShowClearBtn(true);
                    if (refFlatpickr.current) {
                      refFlatpickr.current.flatpickr.setDate([from, to]);
                    }
                    setFilter({
                      ...filter,
                      from_date: from,
                      to_date: to,
                      quick_range: "1y",
                    });
                  }}
                >
                  1y
                </button>
              </div> */}

              {/* Date Range Picker */}
              <div className="analytics-date-display">
                <i className="bx bx-calendar" onClick={handleCalendarClick}></i>
                <span onClick={handleCalendarClick}>
                  {filter.from_date && filter.to_date
                    ? `${moment(filter.from_date, ["DD/MM/YYYY", "YYYY-MM-DD"], true).format("DD/MM/YYYY")} – ${moment(filter.to_date, ["DD/MM/YYYY", "YYYY-MM-DD"], true).format("DD/MM/YYYY")}`
                    : "Select Date Range"}
                </span>
                <div className="analytics-flatpickr-hidden">
                  <Flatpickr
                    ref={refFlatpickr}
                    className="form-control"
                    options={{
                      mode: "range",
                      dateFormat: "d/m/Y",
                      onChange: (selected) => {
                        setShowClearBtn(true);
                        if (
                          selected[0] !== undefined &&
                          selected[1] !== undefined &&
                          selected[1] !== ""
                        ) {
                          setFilter({
                            ...filter,
                            from_date: selected[0]
                              ? moment(selected[0]).format("DD/MM/YYYY")
                              : "",
                            to_date: selected[1]
                              ? moment(selected[1]).format("DD/MM/YYYY")
                              : "",
                            quick_range: "",
                          });
                        }
                      },
                      onClose: (selected) => {
                        if (!selected[1]) {
                          setShowClearBtn(false);
                        }
                      },
                    }}
                    placeholder="Date Range"
                  />
                </div>
                {filter.from_date && filter.to_date && (
                  <i
                    className="bx bx-x analytics-date-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDateRangeClear();
                    }}
                  ></i>
                )}
              </div>

              {/* Hierarchy filters — Super/Country/State/City/Region Admin only (not Circle Admin) */}
              {hasRole([1, 6, 7, 8, 2]) && (
                <div className="filters-btn-wrapper">
                  <button
                    ref={filterBtnRef}
                    className={`filters-toggle-btn ${showFiltersPanel ? "active" : ""} ${activeFilterCount > 0 ? "has-filters" : ""}`}
                    onClick={() => setShowFiltersPanel((prev) => !prev)}
                  >
                    <i className="bx bx-filter-alt"></i>
                    <span>Filters</span>
                    {activeFilterCount > 0 && (
                      <span className="filters-badge">{activeFilterCount}</span>
                    )}
                  </button>

                  {/* Filters Backdrop */}
                  {showFiltersPanel && (
                    <div
                      className="filters-backdrop"
                      onClick={() => setShowFiltersPanel(false)}
                    />
                  )}

                  {/* Filters Dropdown Panel */}
                  {showFiltersPanel && (
                    <div className="filters-dropdown-panel" ref={filterPanelRef}>
                      <div className="filters-panel-header">
                        <span className="filters-panel-title">
                          Filter by Location
                        </span>
                        <div className="filters-panel-header-actions">
                          {activeFilterCount > 0 && (
                            <button
                              type="button"
                              className="filters-clear-all-btn"
                              onClick={() => {
                                handleStateClear();
                                setFilter({
                                  ...filter,
                                  country_id: null,
                                  state_id: null,
                                  city_id: null,
                                  region_id: null,
                                  chapter_id: null,
                                  from_date: "",
                                  to_date: "",
                                  quick_range: "",
                                });
                              }}
                            >
                              Clear All
                            </button>
                          )}
                          <button
                            type="button"
                            className="filters-close-btn"
                            onClick={() => setShowFiltersPanel(false)}
                            title="Close"
                          >
                            <i className="bx bx-x"></i>
                          </button>
                        </div>
                      </div>
                      <div className="filters-panel-body">
                        {hasRole([1]) && (
                          <div className="filter-field">
                            <label className="filter-field-label">Country</label>
                            <Select
                              menuPortalTarget={document.body}
                              styles={selectFilterStyles}
                              name="country_id"
                              isClearable
                              options={countryOptions}
                              placeholder="All Countries"
                              value={
                                countryOptions && Array.isArray(countryOptions)
                                  ? countryOptions.find(
                                      (o) => o.value === filter.country_id,
                                    ) || null
                                  : null
                              }
                              onChange={(selected) => {
                                handleStateClear();
                                setFilter({
                                  ...filter,
                                  country_id: selected?.value,
                                  state_id: null,
                                  city_id: null,
                                  region_id: null,
                                  chapter_id: null,
                                  from_date: "",
                                  to_date: "",
                                  quick_range: "",
                                });
                              }}
                            />
                          </div>
                        )}

                        {hasRole([1, 6]) && (
                          <div className="filter-field">
                            <label className="filter-field-label">State</label>
                            <Select
                              menuPortalTarget={document.body}
                              styles={selectFilterStyles}
                              ref={selectStateRef}
                              name="state_id"
                              isClearable
                              options={stateOptions}
                              placeholder="All States"
                              value={
                                stateOptions && Array.isArray(stateOptions)
                                  ? stateOptions.find(
                                      (o) => o.value === filter.state_id,
                                    ) || null
                                  : null
                              }
                              onChange={(selected) => {
                                handleCityClear();
                                setFilter({
                                  ...filter,
                                  state_id: selected?.value,
                                  city_id: null,
                                  region_id: null,
                                  chapter_id: null,
                                  from_date: "",
                                  to_date: "",
                                  quick_range: "",
                                });
                              }}
                            />
                          </div>
                        )}

                        {hasRole([1, 6, 7]) && (
                          <div className="filter-field">
                            <label className="filter-field-label">City</label>
                            <Select
                              menuPortalTarget={document.body}
                              styles={selectFilterStyles}
                              ref={selectCityRef}
                              name="city_id"
                              isClearable
                              options={cityOptions}
                              placeholder="All Cities"
                              value={
                                cityOptions && Array.isArray(cityOptions)
                                  ? cityOptions.find(
                                      (o) => o.value === filter.city_id,
                                    ) || null
                                  : null
                              }
                              onChange={(selected) => {
                                handleRegionClear();
                                setFilter({
                                  ...filter,
                                  city_id: selected?.value,
                                  region_id: null,
                                  chapter_id: null,
                                  from_date: "",
                                  to_date: "",
                                  quick_range: "",
                                });
                              }}
                            />
                          </div>
                        )}

                        {hasRole([1, 6, 7, 8]) && (
                          <div className="filter-field">
                            <label className="filter-field-label">Region</label>
                            <Select
                              menuPortalTarget={document.body}
                              styles={selectFilterStyles}
                              ref={selectRegionRef}
                              name="region_id"
                              isClearable
                              options={regionOptions}
                              placeholder="All Regions"
                              value={
                                regionOptions && Array.isArray(regionOptions)
                                  ? regionOptions.find(
                                      (o) => o.value === filter.region_id,
                                    ) || null
                                  : null
                              }
                              onChange={(selected) => {
                                handleChapterClear();
                                setFilter({
                                  ...filter,
                                  region_id: selected?.value,
                                  chapter_id: null,
                                  from_date: "",
                                  to_date: "",
                                  quick_range: "",
                                });
                              }}
                            />
                          </div>
                        )}

                        {hasRole([1, 6, 7, 8, 2]) && (
                          <div className="filter-field">
                            <label className="filter-field-label">Circle</label>
                            <Select
                              menuPortalTarget={document.body}
                              styles={selectFilterStyles}
                              ref={selectChapterRef}
                              name="chapter_id"
                              isClearable
                              options={chapterOptions}
                              placeholder="All Circles"
                              value={
                                chapterOptions && Array.isArray(chapterOptions)
                                  ? chapterOptions.find(
                                      (o) => o.value === filter.chapter_id,
                                    ) || null
                                  : null
                              }
                              onChange={(selected) => {
                                if (selected && selected.value) {
                                  const periodMonths = Number(selected.rolling_period) || 12;
                                  const toDate = moment().format("DD/MM/YYYY");
                                  const fromDate = moment().subtract(periodMonths, "months").add(1, "day").format("DD/MM/YYYY");
                                  setFilter({
                                    ...filter,
                                    chapter_id: selected.value,
                                    from_date: fromDate,
                                    to_date: toDate,
                                    quick_range: "",
                                  });
                                  if (refFlatpickr.current && refFlatpickr.current.flatpickr) {
                                    refFlatpickr.current.flatpickr.setDate([fromDate, toDate]);
                                    setShowClearBtn(true);
                                  }
                                } else {
                                  setFilter({
                                    ...filter,
                                    chapter_id: null,
                                    from_date: "",
                                    to_date: "",
                                    quick_range: "",
                                  });
                                  if (refFlatpickr.current && refFlatpickr.current.flatpickr) {
                                    refFlatpickr.current.flatpickr.clear();
                                    setShowClearBtn(false);
                                  }
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Analytics Stat Cards */}
        <AnalyticsCards
          data={data}
          ovalLoading={ovalLoading}
          hasPermission={hasPermission}
        />

        {/* Chart Swiper */}
        <ChartSwiper
          data={data}
          ovalLoading={ovalLoading}
          hasPermission={hasPermission}
        />

        {/* Top Achievers of the Week */}
        <TopAchievers
          data={data}
          ovalLoading={ovalLoading}
          hasPermission={hasPermission}
          filter={filter}
        />
      </div>

      {/* Birthday Card and Pending Lists Row */}
      <div className="row dash-table">
        <PendingListsTables
          data={data}
          loading={loading}
          hasPermission={hasPermission}
        />
        {hasPermission("dashboard-birthday-list-view") && (
          <BirthdayCard data={data} />
        )}
      </div>

      {/* Top 10 Tables */}
      <TopTenTables
        data={data}
        loading={loading}
        hasPermission={hasPermission}
        pdfLoading={pdfLoading}
        handleDownloadPDF={handleDownloadPDF}
      />

      <div className="content-backdrop fade"></div>
    </div>
  );
};

export default Dashboard;
